import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  isAxiosError
} from 'axios'
import axiosRetry, { isNetworkError } from 'axios-retry'
import { useUserStore } from '@/stores/user/profile'
import { useMainStore } from '@/stores/ui/main'
import { useNotificationStore } from '@/stores/ui/notifications'
import { storeToRefs } from 'pinia'
import { injectGAParams } from '@/utils/gaCookies'
import {
  injectOrderToken,
  isOrderForbiddenError,
  ORDER_FORBIDDEN_MESSAGE,
  rememberOrderTokenFromResponse
} from '@/utils/orderAccessToken'

interface FetcherOptions {
  baseUrl?: string
  headers?: Record<string, string>
}

interface FetcherReturn {
  axiosInstance: AxiosInstance
}

/**
 * Custom axios instance with authentication handling
 * @param options - Configuration options for the fetcher
 * @returns Object containing the configured axios instance
 */
export function useFetcher(options: FetcherOptions = {}): FetcherReturn {
  // Set default headers
  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'X-Project': import.meta.env.VITE_PROJECT_ALIAS as string
  }

  // Initialize user store
  const userStore = useUserStore()
  const { token, isLoggedIn } = storeToRefs(userStore)

  // Tours are served by a third-party backend (VITE_APP_API_INSP) — the main account's
  // Bearer token must never be sent there. Identify it by target baseURL, not by the
  // current page path: other, legitimately-authenticated requests to the main backend
  // (e.g. Centrifugo token refresh, user info) can run while a tour page is showing.
  const toursBackendUrl = import.meta.env.VITE_APP_API_INSP as string | undefined
  const isTourBackend = Boolean(toursBackendUrl) && options.baseUrl === toursBackendUrl

  // Clone headers to avoid mutating the original object
  const headersData: Record<string, string> = { ...defaultHeaders }

  // Add authorization header if user is logged in and this instance doesn't target the tours backend
  if (isLoggedIn.value && token.value && !isTourBackend) {
    headersData['Authorization'] = `Bearer ${token.value}`
  }

  // Apply headers to axios defaults
  axios.defaults.headers.common = { ...axios.defaults.headers.common, ...headersData }

  // Create axios instance with provided base URL
  const axiosInstance: AxiosInstance = axios.create({
    baseURL: options.baseUrl,
    headers: {
      ...defaultHeaders,
      ...options.headers
    }
  })

  // Define the response interceptor FIRST
  // (so it executes AFTER axios-retry gives up, since response interceptors run in reverse order of addition)
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Any status code that lie within the range of 2xx cause this function to trigger
      return rememberOrderTokenFromResponse(response)
    },
    (error: unknown) => {
      let mainStore
      let notificationStore

      try {
        mainStore = useMainStore()
        notificationStore = useNotificationStore()
      } catch (e) {
        // Fallback if stores are not available
      }

      // 1. Reset global loading state on any error
      if (mainStore) {
        mainStore.isRequesting = false
      }

      if (isAxiosError(error)) {
        // 2. Handle Network Error
        if (!error.response) {
          notificationStore?.showError('Network error. Please check your internet connection.')
          return Promise.reject(error)
        }

        const status = error.response.status

        // 3. Handle specific error statuses
        if (status === 401) {
          userStore.preventLogout?.()

          const path = window.location.pathname
          if (!path.includes('/account/signin') && !path.includes('/password/reset')) {
            window.location.href = '/account/signin'
          }
        } else if (status >= 500) {
          notificationStore?.showError('Server is temporarily unavailable. Please try again later.')
        } else if (status === 403) {
          notificationStore?.showError(
            isOrderForbiddenError(error)
              ? ORDER_FORBIDDEN_MESSAGE
              : 'You do not have permission to perform this action.'
          )
        }
        // Notice: We don't catch 400 or 422 here to avoid double messages (forms handle them)
      }

      return Promise.reject(error)
    }
  )

  // Add retry logic LAST (so its response interceptor runs FIRST)
  axiosRetry(axiosInstance, {
    retries: 3,
    retryDelay: (retryCount) => retryCount * 1000,
    retryCondition: (error) => {
      if (isNetworkError(error)) return true
      if (error.response && error.response.status >= 500) {
        return error.config?.method?.toLowerCase() === 'get'
      }
      return false
    },
    onRetry: (retryCount, error) => {
      if (import.meta.env.DEV) {
        console.warn(`[Retry] Попытка №${retryCount} для ${error.config?.url}`)
      }
    }
  })

  // Request interceptor for adding auth token and GA parameters
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // axios.defaults.headers.common is a shared mutable object, so Authorization can already be
      // baked in from an earlier request; actively strip it for the tours backend rather than
      // just skipping the assignment below.
      if (isTourBackend) {
        delete config.headers['Authorization']
      } else if (isLoggedIn.value && token.value && !config.headers['Authorization']) {
        config.headers.Authorization = `Bearer ${token.value}`
      }

      // Inject GA parameters (client_id, session_id) for Zoho CRM lead tracking
      injectGAParams(config)

      // DEVA-232: the order secret goes only to the main backend's order routes
      injectOrderToken(config, { skip: isTourBackend })

      return config
    },
    (error: unknown) => {
      return Promise.reject(error)
    }
  )

  return { axiosInstance }
}
